import { useEffect, useId, useRef, type ReactNode } from 'react';
import {
  IconChevronDown,
  IconChevronUp,
  IconPlus,
  IconXMark,
} from '../../components/icons';
import type { StandardChecklist } from './lists';

interface ChildPickerLabels {
  moveUp: string;
  moveDown: string;
  remove: string;
  add: string;
  addHeading: string;
}

type Action = 'up' | 'down' | 'remove' | 'add';

/**
 * The children of a combined list: the picked ones first, in their order, each
 * movable and removable; below them the standard lists still to add, A–Z as
 * given. An added list goes last. Adding stops at `max`. Says so instead of
 * showing an empty box when there are no standard lists at all.
 */
export function ChildPicker({
  label,
  emptyText,
  options,
  value,
  onChange,
  labels,
  max,
}: {
  label: string;
  emptyText: string;
  options: readonly StandardChecklist[];
  value: readonly string[];
  onChange: (ids: string[]) => void;
  labels: ChildPickerLabels;
  max: number;
}) {
  const baseId = useId();
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  /** The button to focus once `value` has changed, so focus never drops to the page. */
  const pendingFocus = useRef<string | null>(null);

  const byId = new Map(options.map((o) => [o.id, o]));
  const picked = value.flatMap((id) => byId.get(id) ?? []);
  const pickedIds = picked.map((p) => p.id);
  const offer = options.filter((o) => !value.includes(o.id));
  const full = picked.length >= max;

  const buttonKey = (id: string, action: Action) => `${id}:${action}`;
  const nameId = (id: string) => `${baseId}-${id}`;

  function commit(ids: string[], focus: string | null) {
    pendingFocus.current = focus;
    onChange(ids);
  }

  function move(index: number, to: number) {
    const ids = [...pickedIds];
    const [id] = ids.splice(index, 1);
    if (id === undefined) return;
    ids.splice(to, 0, id);
    // The pressed arrow is disabled once the row reaches an end: take the other.
    const action: Action =
      to === 0
        ? 'down'
        : to === ids.length - 1
          ? 'up'
          : to < index
            ? 'up'
            : 'down';
    commit(ids, buttonKey(id, action));
  }

  function remove(index: number) {
    const ids = pickedIds.filter((_, i) => i !== index);
    const neighbour = ids[index] ?? ids[index - 1];
    const firstOffer = options.find((o) => !ids.includes(o.id));
    commit(
      ids,
      neighbour !== undefined
        ? buttonKey(neighbour, 'remove')
        : firstOffer
          ? buttonKey(firstOffer.id, 'add')
          : null,
    );
  }

  function add(id: string) {
    const ids = [...pickedIds, id];
    const index = offer.findIndex((o) => o.id === id);
    const rest = offer.filter((o) => o.id !== id);
    const neighbour = rest[index] ?? rest[index - 1];
    commit(
      ids,
      neighbour && ids.length < max
        ? buttonKey(neighbour.id, 'add')
        : buttonKey(id, 'remove'),
    );
  }

  useEffect(() => {
    const key = pendingFocus.current;
    if (!key) return;
    pendingFocus.current = null;
    buttons.current.get(key)?.focus();
  }, [value]);

  const button = (
    id: string,
    action: Action,
    props: {
      className: string;
      label: string;
      disabled?: boolean;
      onClick: () => void;
      icon: ReactNode;
    },
  ) => (
    <button
      ref={(el) => {
        if (el) buttons.current.set(buttonKey(id, action), el);
        else buttons.current.delete(buttonKey(id, action));
      }}
      type="button"
      className={props.className}
      aria-label={props.label}
      aria-describedby={nameId(id)}
      title={props.label}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      {props.icon}
    </button>
  );

  return (
    <fieldset className="child-picker">
      <legend className="field-label">{label}</legend>
      {options.length === 0 ? (
        <p className="text-hint">{emptyText}</p>
      ) : (
        <>
          {picked.length > 0 && (
            <ol className="child-picker-rows">
              {picked.map((child, index) => (
                <li key={child.id} className="child-picker-row">
                  <span className="child-picker-number" aria-hidden="true">
                    {index + 1}.
                  </span>
                  <span id={nameId(child.id)} className="child-picker-name">
                    {child.name}
                  </span>
                  <div className="child-picker-actions">
                    {button(child.id, 'up', {
                      className: 'btn-icon-ghost',
                      label: labels.moveUp,
                      disabled: index === 0,
                      onClick: () => move(index, index - 1),
                      icon: <IconChevronUp />,
                    })}
                    {button(child.id, 'down', {
                      className: 'btn-icon-ghost',
                      label: labels.moveDown,
                      disabled: index === picked.length - 1,
                      onClick: () => move(index, index + 1),
                      icon: <IconChevronDown />,
                    })}
                    {button(child.id, 'remove', {
                      className: 'btn-icon-danger',
                      label: labels.remove,
                      onClick: () => remove(index),
                      icon: <IconXMark />,
                    })}
                  </div>
                </li>
              ))}
            </ol>
          )}
          {offer.length > 0 && (
            <>
              <p className="child-picker-heading">{labels.addHeading}</p>
              <ul className="child-picker-rows">
                {offer.map((option) => (
                  <li key={option.id} className="child-picker-row">
                    <span id={nameId(option.id)} className="child-picker-name">
                      {option.name}
                    </span>
                    <div className="child-picker-actions">
                      {button(option.id, 'add', {
                        className: 'btn-icon-ghost',
                        label: labels.add,
                        disabled: full,
                        onClick: () => add(option.id),
                        icon: <IconPlus />,
                      })}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </fieldset>
  );
}
